import { TeamMember } from "@/models/team-member.model";
import router from "@/router";
import moment from "moment";
import { ActionTree } from "vuex";
import { TeamsState } from ".";
import { RootState } from "..";
import invitationApi from "../../api/invitation.api";
import teamMemberApi from "../../api/teamMember.api";
import teamsApi from "../../api/teams.api";
import i18n from "../../i18n";
import { RoleNames } from "../../models/role.model";
import { ToastMessage } from "../../models/toast-message.model";
import { RootActionTypes } from "../root-actions";
import { RootMutationTypes } from "./../root-mutations";
import { TeamsMutationTypes } from "./teams.mutations";
import * as Sentry from "@/utils/logger.util";

export const TeamsActionTypes = {
  FetchTeams: "FETCH_TEAMS",
  CreateNewTeam: "CREATE_NEW_TEAM",
  GetTeam: "GET_TEAM",
  UpdateTeam: "UPDATE_TEAM",
  GetTeamMembers: "GET_TEAM_MEMBERS",
  CreateTeamMember: "CREATE_TEAM_MEMBER",
  UpdateTeamMember: "UPDATE_TEAM_MEMBER",
  DeleteTeamMember: "DELETE_TEAM_MEMBER",
  ResendInvitation: "RESEND_INVITATION"
};

export const actions: ActionTree<TeamsState, RootState> = {
  async [TeamsActionTypes.FetchTeams]({ commit, dispatch }, selectedPage: number) {
    try {
      const response = await teamsApi.getTeams(selectedPage);
      commit(TeamsMutationTypes.SetTeams, response.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [TeamsActionTypes.CreateNewTeam]({ commit, state, dispatch }) {
    const { name, captain, logo_path, created_at, uniform_color, uniform_color_hex, facebook_profile } = state.team;
    try {
      const response = await teamsApi.postTeam({
        name,
        captain,
        logo_path,
        created_at,
        uniform_color,
        uniform_color_hex,
        facebook_profile
      });
      router.push(`/my-teams/${response.data.data.id}`);
      commit(TeamsMutationTypes.CreateTeam, response.data);
      commit(
        RootMutationTypes.SetToastMessages,
        {
          variant: "success",
          title: i18n.tc("alert.title.success"),
          content: i18n.tc("my_teams.create_success")
        } as ToastMessage,
        { root: true }
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [TeamsActionTypes.UpdateTeam]({ commit, state, dispatch }) {
    const { id, name, captain, logo_path, created_at, uniform_color, uniform_color_hex, facebook_profile } = state.team;
    try {
      const response = await teamsApi.putTeam(id, {
        name,
        captain,
        logo_path,
        created_at,
        uniform_color,
        uniform_color_hex,
        facebook_profile
      });

      commit(TeamsMutationTypes.SetTeams, response.data.data);
      commit(
        RootMutationTypes.SetToastMessages,
        {
          variant: "success",
          title: i18n.tc("alert.title.success"),
          content: i18n.tc("my_teams.update_success")
        } as ToastMessage,
        { root: true }
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [TeamsActionTypes.GetTeam]({ commit, dispatch }, teamId) {
    try {
      const response = await teamsApi.getTeam(teamId);
      commit(TeamsMutationTypes.SetTeam, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [TeamsActionTypes.GetTeamMembers]({ commit, dispatch }, teamId) {
    try {
      const response = await teamMemberApi.getTeamMembers(teamId);
      commit(TeamsMutationTypes.GetTeamMembers, response.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [TeamsActionTypes.CreateTeamMember]({ state, getters, commit, dispatch }, payload: { teamId: number; values: TeamMember }) {
    const { teamId, values } = payload;
    try {
      const response = await teamMemberApi.postTeamMember(teamId, values);
      const newTeamMember = response.data.data;

      commit(TeamsMutationTypes.AddTeamMember, newTeamMember);

      if (newTeamMember.role.name === RoleNames.Captain) {
        commit(TeamsMutationTypes.SetCaptain, { userId: newTeamMember.user_id });
      }

      commit(
        RootMutationTypes.SetToastMessages,
        {
          variant: "success",
          title: i18n.tc("alert.title.success"),
          content: i18n.tc("my_teams.team_members.create_success")
        } as ToastMessage,
        { root: true }
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [TeamsActionTypes.UpdateTeamMember]({ state, commit, dispatch }, payload: { teamId: number; userId: number; values: TeamMember }) {
    const { teamId, userId, values } = payload;
    try {
      const response = await teamMemberApi.putTeamMember(teamId, userId, values);
      const teamMember = response.data.data;

      commit(TeamsMutationTypes.ModifyTeamMember, { userId, teamMember });

      if (teamMember.role.name === RoleNames.Captain) {
        commit(TeamsMutationTypes.SetCaptain, { userId: teamMember.user_id });
      }

      commit(
        RootMutationTypes.SetToastMessages,
        {
          variant: "success",
          title: i18n.tc("alert.title.success"),
          content: i18n.tc("my_teams.team_members.update_success")
        } as ToastMessage,
        { root: true }
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [TeamsActionTypes.DeleteTeamMember]({ commit, dispatch }, payload: { teamId: number; memberId: number }) {
    const { memberId, teamId } = payload;
    try {
      await teamMemberApi.deleteTeamMember(teamId, memberId);
      commit(TeamsMutationTypes.RemoveTeamMember, memberId);
      commit(
        RootMutationTypes.SetToastMessages,
        {
          variant: "success",
          title: i18n.tc("alert.title.success"),
          content: i18n.tc("my_teams.team_members.delete_success")
        } as ToastMessage,
        { root: true }
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [TeamsActionTypes.ResendInvitation]({ state, commit, dispatch }, memberId: number) {
    try {
      await invitationApi.postResendInvitation(memberId);

      const teamMember = state.teamMembers.find(member => member.id === memberId);
      commit(TeamsMutationTypes.ModifyTeamMember, {
        userId: teamMember.user_id,
        teamMember: {
          ...teamMember,
          last_sent_invitation: moment(new Date()).format("YYYY-MM-DD HH:mm:ss")
        }
      });

      commit(
        RootMutationTypes.SetToastMessages,
        {
          variant: "success",
          title: i18n.tc("alert.title.success"),
          content: i18n.tc("my_teams.team_members.resend_invitation_success")
        } as ToastMessage,
        { root: true }
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  }
};
